import { Button } from "@/components/ui/button";
import { Warning, Info, WarningOctagon, Calculator, CheckCircle } from "@phosphor-icons/react";
import { Section, cad, Disclaimer } from "@/components/tax/shared";

const LINE_LABELS = {
  "10100": "Employment income (T4)", "10400": "Other employment income", "11500": "Pension income", "11900": "EI benefits",
  "12000": "Taxable dividends", "12100": "Interest & investment income", "12600": "Net rental income", "12700": "Taxable capital gains",
  "13000": "Other income", "13500": "Net self-employment income", "15000": "Total income",
  "20700": "RPP contributions", "20800": "RRSP deduction", "21200": "Union & professional dues", "21400": "Child care expenses",
  "22215": "Enhanced CPP/CPP2 deduction", "22900": "Other deductions",
  "30000": "Basic personal amount", "30100": "Age amount", "30300": "Spouse amount", "30400": "Eligible dependant",
  "30800": "CPP base contributions", "31200": "EI premiums", "31260": "Canada employment amount", "31400": "Pension income amount",
  "31600": "Disability amount", "32300": "Tuition", "33099": "Medical expenses",
};
const FLAG_STYLE = {
  missing: { cls: "bg-red-50 border-red-200 text-red-900", icon: WarningOctagon, label: "Missing" },
  review: { cls: "bg-amber-50 border-amber-200 text-amber-900", icon: Warning, label: "Professional review" },
  info: { cls: "bg-slate-50 border-slate-200 text-slate-700", icon: Info, label: "Note" },
};

function Lines({ data, testId }) {
  const rows = Object.entries(data || {}).filter(([, v]) => v);
  if (!rows.length) return <p className="text-sm text-slate-500">None</p>;
  return (
    <div className="divide-y divide-slate-100" data-testid={testId}>
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between py-2 text-sm">
          <span className="text-slate-600"><span className="text-[11px] text-slate-400 mr-2 font-mono">{k}</span>{LINE_LABELS[k] || k}</span>
          <span className="font-semibold text-slate-900">{cad(v)}</span>
        </div>
      ))}
    </div>
  );
}

export function Flags({ flags }) {
  if (!flags?.length) return <p className="text-sm text-emerald-700 flex items-center gap-1.5" data-testid="tax-flags-none"><CheckCircle className="h-4 w-4" weight="fill" /> No issues found.</p>;
  return (
    <div className="space-y-2" data-testid="tax-flags">
      {flags.map((f, i) => {
        const s = FLAG_STYLE[f.level] || FLAG_STYLE.info;
        return (
          <div key={i} className={`flex gap-2 border rounded-lg p-3 text-sm ${s.cls}`} data-testid={`tax-flag-${f.code}`}>
            <s.icon className="h-5 w-5 shrink-0" />
            <p><strong className="mr-1">{s.label}:</strong>{f.message}</p>
          </div>
        );
      })}
    </div>
  );
}

function Headline({ c }) {
  const owing = c.balance_owing > 0;
  return (
    <div className="tax-card p-6 md:p-8 border-l-4 border-l-red-600" data-testid="tax-result-headline">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">{owing ? "Estimated balance owing" : "Estimated refund"}</p>
      <p className={`text-4xl sm:text-5xl font-black tracking-tight mt-1 ${owing ? "text-red-600" : "text-emerald-600"}`} data-testid="tax-result-amount">
        {cad(owing ? c.balance_owing : c.refund)}
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 text-sm">
        {[["Total income", c.total_income], ["Net income", c.net_income], ["Taxable income", c.taxable_income], ["Tax deducted", c.tax_deducted]].map(([l, v]) => (
          <div key={l}><p className="text-slate-500 text-xs">{l}</p><p className="font-bold text-slate-900">{cad(v)}</p></div>
        ))}
      </div>
    </div>
  );
}

export function ReviewStep({ ret, onCalculate, calculating, onApprove, approving, locked }) {
  const c = ret.calc;
  return (
    <div className="space-y-5" data-testid="tax-review-step">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-600">
          {c ? <>Calculated {new Date(ret.calc_at).toLocaleString()} with {ret.tax_year} rules (v{ret.calc_rules_version}).</> : "Calculate your estimate when your information is complete."}
          {ret.calc_stale && <span className="ml-2 font-semibold text-red-600" data-testid="tax-calc-stale">Your information changed — recalculate.</span>}
        </p>
        {!locked && <Button data-testid="tax-calculate-btn" disabled={calculating} onClick={onCalculate} className="bg-red-600 hover:bg-red-700 text-white rounded-full px-6">
          <Calculator className="h-4 w-4 mr-1.5" /> {calculating ? "Calculating…" : c ? "Recalculate" : "Calculate my return"}
        </Button>}
      </div>
      {c && <>
        <Headline c={c} />
        <Section title="Items to resolve" subtitle="Missing items block submission. Professional-review items are passed to your preparer."><Flags flags={c.flags} /></Section>
        <div className="grid lg:grid-cols-2 gap-5">
          <Section title="Income"><Lines data={c.income} testId="tax-lines-income" /></Section>
          <Section title="Deductions"><Lines data={c.deductions} testId="tax-lines-deductions" /></Section>
          <Section title="Federal non-refundable credits" subtitle={`Credit base ${cad(c.federal.credit_base)} → ${cad(c.federal.credits)}`}><Lines data={c.federal.credit_lines} testId="tax-lines-credits" /></Section>
          <Section title="Tax calculation">
            <div className="divide-y divide-slate-100 text-sm" data-testid="tax-lines-tax">
              {[["Federal tax", c.federal.net_tax], ["Ontario tax", c.ontario.net_tax], ["Ontario Health Premium", c.ontario.health_premium],
                ["Donation credits (fed + ON)", c.federal.donations_credit + c.ontario.donations_credit], ["Total payable", c.total_payable],
                ["Income tax deducted", c.tax_deducted], ["CPP/EI overpayment", c.payroll.cpp_overpayment + c.payroll.ei_overpayment], ["Instalments", c.instalments]].map(([l, v]) => (
                <div key={l} className="flex justify-between py-2"><span className="text-slate-600">{l}</span><span className="font-semibold">{cad(v)}</span></div>
              ))}
            </div>
          </Section>
        </div>
        {ret.status === "ready_for_review" && (
          <div className="tax-card p-5 flex flex-wrap items-center gap-4 justify-between">
            <p className="text-sm text-slate-700 max-w-xl">I have reviewed this summary. If anything is wrong I'll go back and correct it before continuing.</p>
            <Button data-testid="tax-approve-summary-btn" disabled={approving || ret.calc_stale} onClick={onApprove} className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-6">
              Summary looks right — continue
            </Button>
          </div>
        )}
      </>}
      <Disclaimer />
    </div>
  );
}
